// Spec 122 E1: the single key table for the browse filters — URL writing, URL reading,
// API parameters and reset all derive from it (FIELDS). View, sort and paging are not filters.
import type { BrowseFilters } from './components/BrowseFilterBar.svelte';
import type { DirectoryTreeParams } from './api';

type ApiParams = Partial<Record<keyof DirectoryTreeParams, string | number | boolean>>;

// One entry per filter: everything that differs between filters lives here.
interface FilterField<V> {
  url: string; // URL parameter name
  empty: (activeHouseholdId: string | null) => V;
  fromUrl: (raw: string | null, activeHouseholdId: string | null) => V;
  toUrl: (value: V, activeHouseholdId: string | null) => string | null;
  toApi: (value: V) => ApiParams;
}

const isSet = (value: unknown) => value !== null && value !== undefined && value !== '';

function textField(
  url: string,
  api: keyof DirectoryTreeParams = url as never,
): FilterField<string | null> {
  return {
    url,
    empty: () => null,
    fromUrl: (raw) => raw || null,
    toUrl: (value) => (isSet(value) ? value : null),
    toApi: (value) => (isSet(value) ? { [api]: value } : {}),
  };
}

function sizeField(url: 'minSize' | 'maxSize'): FilterField<number | null> {
  return {
    url,
    empty: () => null,
    fromUrl: (raw) => (raw ? parseInt(raw, 10) : null),
    toUrl: (value) => (value === null ? null : String(value)),
    toApi: (value) => (value === null ? {} : { [url]: value }),
  };
}

const FIELDS: { [K in keyof BrowseFilters]: FilterField<BrowseFilters[K]> } = {
  search: textField('search'),
  category: textField('category'),
  subCategory: textField('subCategory'),
  reviewStatus: {
    url: 'review',
    empty: () => null,
    fromUrl: (raw) => (raw === 'unreviewed' || raw === 'reviewed' ? raw : null),
    toUrl: (value) => value,
    toApi: (value) => (value ? { isReviewed: value === 'reviewed' } : {}),
  },
  fromDate: textField('fromDate'),
  toDate: textField('toDate'),
  fileFormat: textField('format', 'fileFormat'),
  minSize: sizeField('minSize'),
  maxSize: sizeField('maxSize'),
  assignedTo: textField('assignedTo'),
  userTag: textField('userTag'),
  // No parameter = active household; `all` = no household scope.
  householdId: {
    url: 'householdId',
    empty: (active) => active,
    fromUrl: (raw, active) => (raw === 'all' ? null : raw || active),
    toUrl: (value, active) => (value === null ? 'all' : value !== active ? value : null),
    toApi: (value) => (value ? { householdId: value } : {}),
  },
};

// Loose view for the loops; the typed table above is what the compiler checks.
const ENTRIES = Object.entries(FIELDS) as unknown as [keyof BrowseFilters, FilterField<unknown>][];

// Order = order of the URL parameters.
export const BROWSE_FILTER_KEYS = ENTRIES.map(([key]) => key) as readonly (keyof BrowseFilters)[];

export function emptyFilters(activeHouseholdId: string | null): BrowseFilters {
  return Object.fromEntries(
    ENTRIES.map(([key, f]) => [key, f.empty(activeHouseholdId)]),
  ) as unknown as BrowseFilters;
}

export function filtersToQuery(
  filters: BrowseFilters,
  activeHouseholdId: string | null,
): URLSearchParams {
  const params = new URLSearchParams();
  for (const [key, f] of ENTRIES) {
    const value = f.toUrl(filters[key], activeHouseholdId);
    if (value !== null) params.set(f.url, String(value));
  }
  return params;
}

export function filtersFromQuery(search: string, activeHouseholdId: string | null): BrowseFilters {
  const params = new URLSearchParams(search);
  return Object.fromEntries(
    ENTRIES.map(([key, f]) => [key, f.fromUrl(params.get(f.url), activeHouseholdId)]),
  ) as unknown as BrowseFilters;
}

// The filters shared by the list and the tree (Spec 119 E1); empty values are left out.
export function filtersToApiParams(filters: BrowseFilters): DirectoryTreeParams {
  return Object.assign({}, ...ENTRIES.map(([key, f]) => f.toApi(filters[key])));
}

// The household is a setting (ADR-046), not a search filter: it stays as it is.
export function clearAllFilters(filters: BrowseFilters): BrowseFilters {
  return emptyFilters(filters.householdId);
}
